import { logAppInfo } from "@shared/utils/errors/errorLogger";

/**
 * TGProxy is a Proxy wrapper around the window.tg object that provides
 * safe access to the third-party refund protection widget's API.
 *
 * It intercepts all property accesses and method calls to:
 * - Prevent crashes when properties/methods don't exist
 * - Log errors when API calls fail
 * - Provide graceful fallbacks for missing functionality
 *
 * IMPORTANT: Always accesses window.tg dynamically to ensure we get the
 * latest version after the external script loads and replaces the stub.
 */
export class TGProxy<T extends object> {
  private proxy: T;

  constructor(target: T) {
    this.proxy = new Proxy(target, {
      get: (target, prop) => {
        // Always get the latest window.tg instead of using the captured target
        const latestTg =
          typeof window !== "undefined" && window.tg ? window.tg : target;
        const value = latestTg[prop as keyof typeof latestTg];

        // If the property doesn't exist on target, return a safe fallback
        if (value === undefined || value === null) {
          // For function calls, return a no-op function to prevent crashes
          return (...args: unknown[]) => {
            logAppInfo(
              `Attempted to call tg.${String(
                prop
              )}() but property not available`,
              {
                data: { args },
              }
            );
            return undefined;
          };
        }

        // If it's a function, wrap it to catch any errors
        if (typeof value === "function") {
          return (...args: unknown[]) => {
            try {
              return (value as (...args: unknown[]) => unknown).apply(
                latestTg,
                args
              );
            } catch (error) {
              logAppInfo(`Error calling tg.${String(prop)}()`, {
                category: "refundProtection",
                data: { error, args },
              });
              return undefined;
            }
          };
        }

        // For properties, return the value as-is
        return value;
      },

      apply: (target, thisArg, args) => {
        // Handle direct calls to this.tg(...) like this.tg('configure', {...})
        // Always get the latest window.tg for direct calls too
        const latestTg =
          typeof window !== "undefined" && window.tg ? window.tg : target;

        try {
          if (typeof latestTg === "function") {
            return (latestTg as (...args: unknown[]) => unknown).apply(
              thisArg,
              args
            );
          }
        } catch (error) {
          logAppInfo(`Error calling tg() directly`, {
            category: "refundProtection",
            data: { error, args },
          });
        }
        return undefined;
      },
    });
  }

  /**
   * Returns the proxied object
   */
  public getProxy(): T {
    return this.proxy;
  }
}
